/**
 * Localized day and month names for the employee portal.
 *
 * Notes:
 * - For Nepali we use Latin transliteration (the portal is Latin-only by spec).
 * - date-fns shadcn calendar locale falls back to enUS when no native locale exists.
 */
import { useTranslation } from "react-i18next";
import { hu as huLocale, enUS as enLocale } from "date-fns/locale";

const DAYS_LONG = {
  hu: ["vasárnap", "hétfő", "kedd", "szerda", "csütörtök", "péntek", "szombat"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  ne: ["Aaitabar", "Sombar", "Mangalbar", "Budhabar", "Bihibar", "Sukrabar", "Sanibar"],
};

const DAYS_SHORT = {
  hu: ["V", "H", "K", "Sz", "Cs", "P", "Sz"],
  en: ["S", "M", "T", "W", "T", "F", "S"],
  ne: ["Aa", "So", "Ma", "Bu", "Bi", "Su", "Sa"],
};

const MONTHS_LONG = {
  hu: [
    "január", "február", "március", "április", "május", "június",
    "július", "augusztus", "szeptember", "október", "november", "december",
  ],
  en: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ],
  ne: [
    "Janavari", "Phebruari", "Marca", "April", "Me", "Jun",
    "Julai", "Agasta", "Septembar", "Aktobar", "Novembar", "Disembar",
  ],
};

const DATE_FNS_LOCALE = { hu: huLocale, en: enLocale, ne: enLocale };

function pickLang(lang) {
  if (!lang) return "hu";
  const base = lang.split("-")[0];
  return DAYS_LONG[base] ? base : "hu";
}

export function useLocalizedDates() {
  const { i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  return {
    lang,
    days: DAYS_LONG[lang],
    daysShort: DAYS_SHORT[lang],
    months: MONTHS_LONG[lang],
    dateFnsLocale: DATE_FNS_LOCALE[lang],
  };
}

// Pure helpers (no hook) — useful inside utility functions.
export function getDays(lang) {
  return DAYS_LONG[pickLang(lang)];
}
export function getDaysShort(lang) {
  return DAYS_SHORT[pickLang(lang)];
}
export function getMonths(lang) {
  return MONTHS_LONG[pickLang(lang)];
}
export function getDateFnsLocale(lang) {
  return DATE_FNS_LOCALE[pickLang(lang)];
}
